/**
 * ContentLibrary — DE-171 content service admin UI (Phase 2E: authoring-only).
 *
 * Super-admin only (the library is global/super-admin-curated for v1; the
 * schema is tenant-ready). Item-centric authoring; DEPLOY lives on the
 * Releases page (a content profile ships as the `content` part of a release):
 *   • Profiles — immutable, auto-versioned bundles. Each shows its version
 *                history; compose a new one or copy an existing profile.
 *   • Library  — logical ITEMS (a testimonial = its .mp4+.jpg+.json; a song =
 *                .mp3+.png) as preview cards. Multi-select + bulk Move. Storage
 *                (the upload staging pool) first.
 *
 * Writes are super-admin-gated server-side (RLS + requireRole); this mirrors
 * that with a Forbidden card.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useContentItems,
  useContentProfiles,
  useCreateContentProfile,
  useRetireProfile,
  useMe,
  useMoveItem,
  useProfileDetail,
  type ContentItem,
  type ContentProfile,
} from '../api/hooks';
import { MediaPreviewModal } from '../components/MediaPreviewModal';
import { fmtBytes, fmtMSS, KIND_GLYPH } from '../components/format';
import { useToast } from '../components/Toast';
import { UploadModal } from './UploadModal';
import { ItemMetadataModal } from './ItemMetadataModal';

// Device-scanned collections an item can be Moved into (Storage is the upload
// staging pool; 'core' is never a target — its flat clips are enforced device-side).
const DEVICE_COLLECTIONS = ['media-player', 'testimonials', 'artome-presentation', 'audio'];
const MOVE_TARGETS = [...DEVICE_COLLECTIONS, 'storage'];

function groupItems(items: ContentItem[]): Record<string, ContentItem[]> {
  const out: Record<string, ContentItem[]> = {};
  for (const it of items) (out[it.collection] ??= []).push(it);
  return out;
}

export function ContentLibrary() {
  const me = useMe();
  const toast = useToast();
  const retireProfile = useRetireProfile();
  const items = useContentItems();
  const profiles = useContentProfiles();
  const [composeOpen, setComposeOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [copyFrom, setCopyFrom] = useState<ContentProfile | null>(null);
  const [viewProfile, setViewProfile] = useState<ContentProfile | null>(null);
  // Copy → fetch the source profile's item ids, then open the Compose form seeded.
  const copySeed = useProfileDetail(copyFrom?.id ?? null);
  useEffect(() => {
    if (copyFrom && copySeed.isError) {
      toast.error(`Couldn't load ${copyFrom.name} to copy: ${(copySeed.error as Error).message}`);
      setCopyFrom(null);
    }
  }, [copyFrom, copySeed.isError, copySeed.error, toast]);

  if (me.data && me.data.role !== 'super-admin') {
    return (
      <div className="card max-w-md">
        <h2 className="font-semibold mb-1">Forbidden</h2>
        <p className="text-sm text-muted">The content library is curated by super-admins.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Content</h1>
          <p className="text-sm text-muted">
            Media library and versioned content bundles. Deploy a bundle from the Releases page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="px-3 py-1.5 text-sm font-medium text-muted hover:text-fg"
          >
            How it works
          </button>
          <Link
            to="/releases"
            className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft"
          >
            Deploy on Releases →
          </Link>
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft"
          >
            Upload
          </button>
          <button
            type="button"
            onClick={() => setComposeOpen(true)}
            className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90"
          >
            New profile
          </button>
        </div>
      </div>

      <ProfilesSection
        loading={profiles.isLoading}
        error={profiles.error as Error | null}
        profiles={profiles.data?.profiles ?? []}
        onCopy={setCopyFrom}
        onView={setViewProfile}
        onRetire={(p) => {
          // Confirm by NAME, not a generic "are you sure": the list shows several
          // versions of the same profile and they differ only by a number.
          if (!window.confirm(
            `Retire ${p.name} v${p.version}?\n\n`
            + 'It disappears from the library and can no longer be deployed. '
            + 'Deployment history is kept.')) return;

          // A profile that is a device's CURRENT content is refused first (409),
          // then offered again with force. Two steps on purpose: the operator
          // should learn WHICH devices are affected before deciding, and the
          // server's message already names them — so it is shown verbatim rather
          // than paraphrased into something that could drift from the truth.
          const run = (force: boolean) =>
            retireProfile.mutate({ id: p.id, force }, {
              onSuccess: (res) => toast.success(
                `Retired ${p.name} v${p.version}${res.forced ? ' (was live)' : ''}`),
              onError: (e: unknown) => {
                const err = e as { status?: number; message: string };
                if (!force && err.status === 409) {
                  if (window.confirm(
                    `${err.message}\n\nRetire ${p.name} v${p.version} anyway?`)) run(true);
                  return;
                }
                toast.error(err.message);
              },
            });
          run(false);
        }}
        retiringId={retireProfile.isPending
          ? (retireProfile.variables as { id: string }).id
          : null}
        copyingId={copySeed.isFetching ? copyFrom?.id ?? null : null}
      />

      <LibrarySection
        items={items.data?.items ?? []}
        loading={items.isLoading}
        error={items.error as Error | null}
      />

      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
      {uploadOpen && <UploadModal onClose={() => setUploadOpen(false)} />}
      {composeOpen && (
        <ComposeModal items={items.data?.items ?? []} onClose={() => setComposeOpen(false)} />
      )}
      {copyFrom && copySeed.data && (
        <ComposeModal
          key={copyFrom.id}
          items={items.data?.items ?? []}
          seed={{
            name: copySeed.data.name,
            sourceVersion: copySeed.data.version,
            minAppVersion: copySeed.data.minAppVersion,
            minOsVersion: copySeed.data.minOsVersion,
            itemIds: copySeed.data.itemIds,
          }}
          onClose={() => setCopyFrom(null)}
        />
      )}
      {viewProfile && (
        <ProfileViewModal
          profile={viewProfile}
          items={items.data?.items ?? []}
          onClose={() => setViewProfile(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Profiles — the authoring object: version history + Copy. Deploy lives on
// the Releases page (a profile ships as a release's `content` part).
// ─────────────────────────────────────────────────────────────────────────
function ProfilesSection({
  profiles,
  loading,
  error,
  onCopy,
  onView,
  onRetire,
  retiringId,
  copyingId,
}: {
  profiles: ContentProfile[];
  loading: boolean;
  error: Error | null;
  onCopy: (p: ContentProfile) => void;
  onView: (p: ContentProfile) => void;
  onRetire: (p: ContentProfile) => void;
  retiringId: string | null;
  copyingId: string | null;
}) {
  // Group versions under their name; newest version first (profiles arrive
  // name-asc then created_at-desc from the backend).
  const byName = useMemo(() => {
    const m = new Map<string, ContentProfile[]>();
    for (const p of profiles) {
      const arr = m.get(p.name) ?? [];
      arr.push(p);
      m.set(p.name, arr);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [profiles]);

  return (
    <section className="card">
      <h2 className="font-medium mb-3">Profiles</h2>
      {loading && <p className="text-sm text-muted">Loading…</p>}
      {error && <p className="text-sm text-warn">{error.message}</p>}
      {!loading && !error && profiles.length === 0 && (
        <p className="text-sm text-muted">No profiles yet. Compose one from the library below.</p>
      )}
      <div className="space-y-4">
        {byName.map(([name, versions]) => (
          <div key={name} className="border border-border rounded-md p-3">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="font-semibold">{name}</h3>
              <span className="text-xs text-muted">
                {versions.length} version{versions.length === 1 ? '' : 's'}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted border-b border-border">
                  <th className="font-medium py-1.5 pr-4">Version</th>
                  <th className="font-medium py-1.5 pr-4">Items</th>
                  <th className="font-medium py-1.5 pr-4">Min app</th>
                  <th className="font-medium py-1.5 pr-4">Min Mender</th>
                  <th className="font-medium py-1.5" />
                </tr>
              </thead>
              <tbody>
                {versions.map((p, i) => (
                  <tr key={p.id} className="border-b border-border last:border-0 hover:bg-soft">
                    <td className="py-1.5 pr-4 font-mono text-xs">
                      v{p.version}
                      {i === 0 && <span className="ml-1 pill-muted">latest</span>}
                    </td>
                    <td className="py-1.5 pr-4">{p.asset_count}</td>
                    <td className="py-1.5 pr-4 font-mono text-xs">
                      {p.min_app_version ? `≥ ${p.min_app_version}` : '—'}
                    </td>
                    <td className="py-1.5 pr-4 font-mono text-xs">
                      {p.min_os_version ? `≥ ${p.min_os_version}` : '—'}
                    </td>
                    <td className="py-1.5 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => onView(p)}
                          title="View this profile's contents (read-only)"
                          className="px-2.5 py-1 border border-border rounded-md text-xs font-medium hover:bg-white"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => onCopy(p)}
                          disabled={copyingId === p.id}
                          title="Duplicate this profile into a new editable draft"
                          className="px-2.5 py-1 border border-border rounded-md text-xs font-medium hover:bg-white disabled:opacity-50"
                        >
                          {copyingId === p.id ? 'Copying…' : 'Copy'}
                        </button>
                        <button
                          type="button"
                          onClick={() => onRetire(p)}
                          disabled={retiringId === p.id}
                          title="Retire this profile — hides it and blocks new deploys. Deployment history is kept, and it is refused while any device or group still has it."
                          className="px-2.5 py-1 border border-border rounded-md text-xs font-medium hover:bg-white disabled:opacity-50"
                        >
                          {retiringId === p.id ? 'Retiring…' : 'Retire'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Library — logical items as preview cards, multi-select + bulk move.
// ─────────────────────────────────────────────────────────────────────────
function LibrarySection({
  items,
  loading,
  error,
}: {
  items: ContentItem[];
  loading: boolean;
  error: Error | null;
}) {
  const toast = useToast();
  const move = useMoveItem();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<ContentItem | null>(null);
  const [preview, setPreview] = useState<ContentItem | null>(null);
  const grouped = useMemo(() => groupItems(items), [items]);
  const collections = Object.keys(grouped).sort((a, b) =>
    a === 'storage' ? -1 : b === 'storage' ? 1 : a.localeCompare(b),
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const moveItems = async (itemIds: string[], collection: string) => {
    const results = await Promise.allSettled(
      itemIds.map((itemId) => move.mutateAsync({ itemId, collection })),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    if (ok) toast.success(`Moved ${ok} item${ok === 1 ? '' : 's'} → ${collection}`);
    if (failed) toast.error(`${failed} move${failed === 1 ? '' : 's'} failed`);
    setSelected(new Set());
  };

  return (
    <section className="card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="font-medium">Library</h2>
        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted">{selected.size} selected</span>
            <select
              value=""
              disabled={move.isPending}
              onChange={(e) => {
                if (e.target.value) void moveItems([...selected], e.target.value);
              }}
              className="px-2 py-1 border border-border rounded-md text-xs bg-white disabled:opacity-50"
              title="Move selected items to a collection"
            >
              <option value="">Move {selected.size} to…</option>
              {MOVE_TARGETS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-xs text-muted hover:text-fg"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {loading && <p className="text-sm text-muted">Loading…</p>}
      {error && <p className="text-sm text-warn">{error.message}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="text-sm text-muted">
          No content yet. Upload media (lands in Storage) or seed the factory manifest.
        </p>
      )}

      <div className="space-y-5">
        {collections.map((coll) => {
          const list = grouped[coll] ?? [];
          const isStorage = coll === 'storage';
          // Testimonials are portrait + numerous → pack more per row (smaller
          // thumbnails) so a growing library stays scannable. Others keep 16:9 4-up.
          const gridCols =
            coll === 'testimonials'
              ? 'grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8'
              : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';
          return (
            <div key={coll}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted mb-2">
                {coll} <span className="text-muted/70">({list.length})</span>
                {isStorage && (
                  <span className="text-muted/70 normal-case font-normal">
                    {' '}· staging — Move into a collection before deploying
                  </span>
                )}
              </h3>
              <div className={`grid gap-3 ${gridCols}`}>
                {list.map((it) => (
                  <ItemCard
                    key={it.itemId}
                    item={it}
                    selected={selected.has(it.itemId)}
                    onToggle={() => toggle(it.itemId)}
                    onPlay={() => setPreview(it)}
                    onEdit={() => setEditing(it)}
                    onMove={(collection) => void moveItems([it.itemId], collection)}
                    moving={move.isPending}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {preview && (
        <MediaPreviewModal
          assetId={preview.primaryAssetId}
          kind={preview.kind}
          title={preview.title}
          posterUrl={preview.thumbnailUrl ?? undefined}
          description={preview.description ?? undefined}
          onClose={() => setPreview(null)}
        />
      )}

      {editing && (
        <ItemMetadataModal item={editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

function ItemCard({
  item,
  selected,
  onToggle,
  onPlay,
  onMove,
  onEdit,
  moving,
}: {
  item: ContentItem;
  onEdit: () => void;
  selected: boolean;
  onToggle: () => void;
  onPlay: () => void;
  onMove: (collection: string) => void;
  moving: boolean;
}) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const showThumb = item.thumbnailUrl && !thumbFailed;
  // Click the thumbnail to PLAY (managed video/audio); select moves to the ✓ box.
  const playable = item.managed && (item.kind === 'video' || item.kind === 'audio');
  // Testimonial posters are portrait (9:16) — give them a portrait box + object-contain
  // so the real .jpg shows uncropped (vs landscape video frames, which fill 16:9).
  const portrait = item.collection === 'testimonials';
  return (
    <div
      className={`border rounded-lg overflow-hidden bg-white transition-colors ${
        selected ? 'border-accent ring-1 ring-accent' : 'border-border'
      }`}
    >
      <div className={`relative bg-soft ${portrait ? 'aspect-[9/16]' : 'aspect-video'}`}>
        {showThumb ? (
          <img
            src={item.thumbnailUrl!}
            alt={item.title}
            loading="lazy"
            onError={() => setThumbFailed(true)}
            className={`absolute inset-0 w-full h-full ${portrait ? 'object-contain' : 'object-cover'}`}
          />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-3xl text-muted/50 pointer-events-none" aria-hidden="true">
            {KIND_GLYPH[item.kind] ?? '≡'}
          </span>
        )}

        {/* Thumbnail click → play (playable items only); ▶ signals it. */}
        {playable && (
          <button
            type="button"
            onClick={onPlay}
            aria-label={`Play ${item.title}`}
            title="Preview"
            className="group absolute inset-0 flex items-center justify-center focus:outline-none"
          >
            <span className="grid h-11 w-11 place-items-center rounded-full bg-black/45 text-white text-xl shadow-sm transition group-hover:bg-black/65 group-hover:scale-105 group-focus-visible:bg-black/65 group-focus-visible:ring-2 group-focus-visible:ring-white" aria-hidden="true">
              ▶
            </span>
          </button>
        )}

        {/* Select checkbox — sits above the play button so it wins the click. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          aria-label={selected ? 'Deselect' : 'Select'}
          aria-pressed={selected}
          className={`absolute top-1.5 left-1.5 z-10 h-5 w-5 rounded border flex items-center justify-center text-[11px] ${
            selected ? 'bg-accent border-accent text-white' : 'bg-white/80 border-border hover:bg-white'
          }`}
        >
          {selected ? '✓' : ''}
        </button>

        {item.duration_s !== null && (
          <span className="absolute bottom-1.5 right-1.5 px-1 rounded bg-black/60 text-white text-[10px] font-mono pointer-events-none">
            {fmtMSS(item.duration_s)}
          </span>
        )}
      </div>
      <div className="p-2 space-y-1">
        <div className="text-xs font-medium truncate" title={item.title}>
          {item.title}
        </div>
        {item.description && (
          <div className="text-[11px] text-muted line-clamp-2" title={item.description}>
            {item.description}
          </div>
        )}
        <div className="text-[11px] text-muted">
          {fmtBytes(item.totalBytes)} · {item.fileCount} file{item.fileCount === 1 ? '' : 's'}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <span className={item.managed ? 'pill-accent' : 'pill-muted'}>
            {item.managed ? 'managed' : 'factory'}
          </span>
          {item.videoCodec === 'h264' && <span className="pill-muted">H.264</span>}
          {item.videoCodec === 'hevc' && <span className="pill-accent">H.265</span>}
          {/* DE-234 — the SPOKEN language. Shown only when it is KNOWN: an absent
              value means unknown, and a "—" chip would read as a fact when it is
              the absence of one. The device treats unknown as "never filter", so
              a blank here is genuinely different from a wrong value. */}
          {item.language && (
            <span className="pill-muted" title="Spoken language of this item">
              {item.language.toUpperCase()}
            </span>
          )}
          {/* Which subtitle tracks exist. Derived from the registered files, so
              this is what a device would actually find. */}
          {item.subtitleLangs?.map((l) => (
            <span key={l} className="pill-accent" title={`Subtitle track: ${l}`}>
              CC {l.toUpperCase()}
            </span>
          ))}
          {!item.complete && <span className="pill-warn">incomplete</span>}
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="shrink-0 px-1.5 py-1 border border-border rounded-md text-[11px] hover:bg-[var(--surface-2)]"
            title="View and edit metadata — title, description, spoken language, translations"
          >
            Edit
          </button>
          <select
            value=""
            disabled={moving}
            onChange={(e) => {
              if (e.target.value) onMove(e.target.value);
            }}
            className="w-full px-1.5 py-1 border border-border rounded-md text-[11px] bg-white disabled:opacity-50"
            title="Move to a collection"
          >
            <option value="">Move to…</option>
            {MOVE_TARGETS.filter((t) => t !== item.collection).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Compose — select complete ITEMS (expands to file asset ids server-side).
// ─────────────────────────────────────────────────────────────────────────
interface ComposeSeed {
  name: string;
  sourceVersion: string;
  minAppVersion: string | null;
  minOsVersion: string | null;
  itemIds: string[];
}

function ComposeModal({
  items,
  seed,
  onClose,
}: {
  items: ContentItem[];
  seed?: ComposeSeed;
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateContentProfile();
  // When seeded (Copy), pre-fill name + min-app + selection from the source.
  // Version stays blank so it auto-increments (same name → next version; the
  // source version would 409 against the immutable (name,version) constraint).
  const [name, setName] = useState(seed?.name ?? '');
  const [version, setVersion] = useState('');
  const [minAppVersion, setMinAppVersion] = useState(seed?.minAppVersion ?? '');
  const [minOsVersion, setMinOsVersion] = useState(seed?.minOsVersion ?? '');
  // Seed only the items that still exist in the library (a since-deleted item
  // just isn't pre-ticked). Lazy init — runs once; remounted per copy via key.
  const [selected, setSelected] = useState<Set<string>>(() => {
    if (!seed) return new Set();
    const avail = new Set(items.map((i) => i.itemId));
    return new Set(seed.itemIds.filter((id) => avail.has(id)));
  });

  // Profiles never contain the Storage staging pool; incomplete items can't be
  // selected (the backend would 400 anyway).
  const grouped = useMemo(
    () => groupItems(items.filter((it) => it.collection !== 'storage')),
    [items],
  );
  const collections = Object.keys(grouped).sort();

  const toggle = (it: ContentItem) => {
    if (!it.complete) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(it.itemId)) next.delete(it.itemId);
      else next.add(it.itemId);
      return next;
    });
  };

  // Expand selected items → their file asset ids.
  const selectedAssetIds = useMemo(() => {
    const ids: string[] = [];
    for (const it of items) if (selected.has(it.itemId)) ids.push(...it.fileAssetIds);
    return ids;
  }, [items, selected]);

  // ── partial-collection warning (DE-247) ──
  // A profile is a SNAPSHOT of each collection it touches, not a patch: on the
  // device, `files` is the complete set for that collection's new version, so
  // anything not listed is gone from it after the apply. Collections the profile
  // does not mention are untouched.
  //
  // Nothing said so, and the composer happily lets you tick one item and deploy
  // it — which would reduce that collection to that one item. This computes the
  // coverage per touched collection so the gap is visible BEFORE creating the
  // profile rather than discovered on a device.
  const partial = useMemo(() => {
    const out: { collection: string; picked: number; total: number }[] = [];
    for (const c of collections) {
      const all = grouped[c] ?? [];
      const picked = all.filter((it) => selected.has(it.itemId)).length;
      if (picked > 0 && picked < all.length)
        out.push({ collection: c, picked, total: all.length });
    }
    return out;
  }, [collections, grouped, selected]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || selected.size === 0) {
      toast.error('Name and at least one item are required.');
      return;
    }
    create.mutate(
      {
        name,
        version: version || undefined, // optional → backend derives next
        minAppVersion: minAppVersion || undefined,
        minOsVersion: minOsVersion || undefined,
        assetIds: selectedAssetIds,
      },
      {
        onSuccess: (res) => {
          toast.success(`Profile ${name} v${res.version} created (${selected.size} items).`);
          onClose();
        },
        onError: (err) => toast.error(`Create failed: ${(err as Error).message}`),
      },
    );
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal">
      <form onSubmit={submit} className="card w-full max-w-2xl space-y-4 max-h-[90vh] overflow-auto">
        <div>
          <h2 className="font-semibold">{seed ? 'Duplicate content profile' : 'New content profile'}</h2>
          <p className="text-sm text-muted">
            {seed ? (
              <>
                Copying <span className="font-medium text-fg">{seed.name}</span>{' '}
                <span className="font-mono text-xs">v{seed.sourceVersion}</span>. Keep the name to save
                the next version; rename to fork a new profile. Edit the selection below, then create.
              </>
            ) : (
              <>
                Profiles are immutable. Leave version blank to auto-increment. Select whole items —
                incomplete items (missing poster/metadata) can't be added.
              </>
            )}
          </p>
          {/* DE-247 — the single sharpest edge in the content system, and nothing
              stated it. A profile is a SNAPSHOT of every collection it touches:
              on the device the manifest's file list is the COMPLETE set for that
              collection's new version, so anything not listed is gone from it
              after the apply. Collections the profile does not mention are left
              alone. Ticking one item and deploying it would reduce that
              collection to that item. */}
          <div className="mt-2 rounded-md border border-border bg-soft p-2">
            <p className="text-xs">
              A profile is a <span className="font-medium">snapshot of each collection it
              touches</span>, not a patch.
            </p>
            <ul className="text-[11px] text-muted mt-1 space-y-0.5 list-disc list-inside">
              <li>Include <span className="font-medium">every</span> item a collection should
                  have — on the device, anything not listed is removed from that collection.</li>
              <li>Collections you don't select from are left untouched.</li>
              <li>So to add one subtitle, select the whole collection — not just that item.</li>
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-xs text-muted">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="emea-standard"
              required
              className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted">Version (optional)</span>
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="auto"
              className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm font-mono"
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted">Min app version</span>
            <input
              value={minAppVersion}
              onChange={(e) => setMinAppVersion(e.target.value)}
              placeholder="optional, e.g. 1.21.0"
              className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm font-mono"
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted">Min Mender version</span>
            <input
              value={minOsVersion}
              onChange={(e) => setMinOsVersion(e.target.value)}
              placeholder="optional, e.g. 2.2.0"
              className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm font-mono"
            />
          </label>
        </div>

        <div className="space-y-3">
          <span className="text-xs text-muted">Items ({selected.size} selected)</span>
          {collections.length === 0 && (
            <p className="text-sm text-muted">No deployable items in the library yet.</p>
          )}
          {collections.map((coll) => {
            const list = grouped[coll] ?? [];
            return (
              <div key={coll} className="border border-border rounded-md p-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">
                  {coll} ({list.length})
                </div>
                <div className="grid grid-cols-2 gap-x-4">
                  {list.map((it) => (
                    <label
                      key={it.itemId}
                      className={`flex items-center gap-2 text-sm py-0.5 ${
                        it.complete ? '' : 'opacity-50'
                      }`}
                      title={it.complete ? it.title : `${it.title} — incomplete, can't deploy`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(it.itemId)}
                        disabled={!it.complete}
                        onChange={() => toggle(it)}
                      />
                      <span className="truncate">{it.title}</span>
                      {!it.complete && <span className="pill-warn shrink-0">incomplete</span>}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {partial.length > 0 && (
          <div className="rounded-md border border-[var(--warn)] p-2">
            <p className="text-xs font-medium">
              Partial collection{partial.length > 1 ? 's' : ''} selected
            </p>
            <ul className="text-[11px] mt-1 space-y-0.5">
              {partial.map((c) => (
                <li key={c.collection}>
                  <span className="font-mono">{c.collection}</span>: {c.picked} of {c.total} items
                  {' '}— deploying this removes the other {c.total - c.picked} from the device.
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-muted mt-1">
              Intentional for a deliberately reduced set. If you only meant to add or change
              something, select the rest of the collection too.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'Create profile'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Profile viewer — READ-ONLY contents of a profile. Reuses the Copy data path
// (useProfileDetail → itemIds) but renders the items instead of seeding a draft,
// so an operator can inspect a profile without the Copy side effect. Resolves
// the profile's item ids against the already-loaded library — no extra fetch.
// ─────────────────────────────────────────────────────────────────────────
function ProfileViewModal({
  profile,
  items,
  onClose,
}: {
  profile: ContentProfile;
  items: ContentItem[];
  onClose: () => void;
}) {
  const detail = useProfileDetail(profile.id);
  const inProfile = useMemo(() => {
    const ids = new Set(detail.data?.itemIds ?? []);
    return groupItems(items.filter((it) => ids.has(it.itemId)));
  }, [items, detail.data]);
  const collections = Object.keys(inProfile).sort();
  const itemCount = detail.data?.itemIds.length ?? 0;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal" onClick={onClose}>
      <div
        className="card w-full max-w-2xl space-y-4 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between gap-2">
          <div>
            <h2 className="font-semibold">
              {profile.name} <span className="font-mono text-xs text-muted">v{profile.version}</span>
            </h2>
            <p className="text-sm text-muted">
              Read-only contents{itemCount ? ` · ${itemCount} item${itemCount === 1 ? '' : 's'}` : ''}. To
              edit, use Copy (creates a new version).
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-fg text-sm">
            Close
          </button>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="pill-muted">
            Min app {profile.min_app_version ? `≥ ${profile.min_app_version}` : '— none'}
          </span>
          <span className="pill-muted">
            Min Mender {profile.min_os_version ? `≥ ${profile.min_os_version}` : '— none'}
          </span>
        </div>

        {detail.isLoading && <p className="text-sm text-muted">Loading contents…</p>}
        {detail.isError && <p className="text-sm text-warn">{(detail.error as Error).message}</p>}
        {detail.data && collections.length === 0 && (
          <p className="text-sm text-muted">This profile has no items.</p>
        )}

        <div className="space-y-3">
          {collections.map((coll) => {
            const list = inProfile[coll] ?? [];
            return (
              <div key={coll} className="border border-border rounded-md p-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">
                  {coll} ({list.length})
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {list.map((it) => (
                    <div
                      key={it.itemId}
                      className="flex items-center gap-2 text-sm py-0.5"
                      title={it.title}
                    >
                      <span className="text-muted" aria-hidden="true">{KIND_GLYPH[it.kind] ?? '≡'}</span>
                      <span className="truncate">{it.title}</span>
                      {it.videoCodec === 'hevc' && <span className="pill-accent shrink-0">H.265</span>}
                      {it.videoCodec === 'h264' && <span className="pill-muted shrink-0">H.264</span>}
                      {!it.complete && <span className="pill-warn shrink-0">incomplete</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// In-context operator help — the concise "how publishing works" walkthrough.
// The full guide lives in docs/content-management-guide.md.
function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal" onClick={onClose}>
      <div
        className="card w-full max-w-lg space-y-4 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <h2 className="font-semibold">How publishing works</h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-fg text-sm">
            Close
          </button>
        </div>

        <ol className="space-y-3 text-sm">
          <li>
            <span className="font-medium">1 · Add content.</span>{' '}
            <span className="text-muted">
              <span className="font-medium text-fg">Upload</span> a video/audio file → it lands in{' '}
              <span className="font-mono text-xs">Storage</span> and is auto-converted to the
              device format with a preview. Then use <span className="font-medium text-fg">Move to…</span>{' '}
              to put it in a collection (media-player / testimonials / artome-presentation / audio) —
              that's what makes it deployable. Items in Storage are never sent to devices.
            </span>
          </li>
          <li>
            <span className="font-medium">2 · Build a set.</span>{' '}
            <span className="text-muted">
              <span className="font-medium text-fg">New profile</span> → tick the items you want. A
              profile is a named, versioned set = the content a device should run. (Leave the version
              blank to auto-number.)
            </span>
          </li>
          <li>
            <span className="font-medium">3 · Publish.</span>{' '}
            <span className="text-muted">
              Deploy happens on the <span className="font-medium text-fg">Releases</span> page: a
              content profile ships as the <span className="font-medium text-fg">content</span> part
              of a release (on its own, or bundled with an OS / app / firmware update) to whole{' '}
              <span className="font-medium text-fg">device groups</span> (the fleet) and/or{' '}
              <span className="font-medium text-fg">single devices</span> (for testing). Track
              progress there.
            </span>
          </li>
          <li>
            <span className="font-medium">4 · Update or retire.</span>{' '}
            <span className="text-muted">
              Deploy a new profile/version from the Releases page to replace what's there (the device
              swaps the whole set; roll back by deploying an older version). To take an item out of
              circulation, <span className="font-medium text-fg">Move</span> it back to Storage.
            </span>
          </li>
        </ol>

        <div className="border-t border-border pt-3 text-xs text-muted space-y-1">
          <p className="font-medium text-fg">Good to know</p>
          <p>• An <span className="font-medium">item</span> bundles a clip's files (video + poster + title) and is always moved/deployed as a unit.</p>
          <p>• Bundles are <span className="font-medium">deployed from the Releases page</span> — to a device group, or to a single device as an override (most-recent deploy wins).</p>
          <p>• <span className="font-medium">Factory</span> (shipped) clips already play on the device and can't be re-deployed — deploy uploaded content.</p>
          <p>• Deploying never interrupts a running demo; content applies at the next mains connection.</p>
        </div>

        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
